import { useState, useEffect, useRef, useCallback } from "react";
import {
  Search,
  MoreVertical,
  Eye,
  Box,
  FileUp,
  X,
  CheckCircle2,
  List,
  RefreshCcw,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../../store";
import {
  getFloorBatches,
  getFloorStock,
  finalizeOutwardMaterials,
  finalizeOutwardProducts,
} from "../../../store/slices/Factory/newFloorSlice";
import PaginationControls from "../../../utils/PaginationControls";
import { useSearchParams } from "react-router-dom";
import { setCurrentPage } from "../../../store/slices/Factory/excelSlice";
import { FloorStockManager } from "./FloorStockManager";
import { getStatusColor, IFloorRequest } from "../../../models/floor.model";
import FloorBatchEditDetailsModal from "./FloorBatchEditModal";
import OutwardStarterPage from "./OutwardModal";
import OutwardExcelUploadModal from "./OutwardExcelUploadModal";
import { BatchesViewModal } from "./OutwardBatchesModal";
import ConfirmationModal from "../../../components/Assets/ConfirmationModal";
import FloorMaterialReturnRequestModal from "./FloorMaterialReturnRequestModal";

const NewFloorStock = () => {
  const dispatch: RootDispatch = useDispatch();
  const pagination = useSelector(
    (state: RootState) => state.newFloor.pagination,
  );
  const requests = useSelector((state: RootState) => state.newFloor.batches);

  const loading = useSelector(
    (state: RootState) => state.newFloor.loading.getFloorBatchesLoading,
  );
  const finalizeOutwardMaterialsLoading = useSelector(
    (state: RootState) =>
      state.newFloor.loading.finalizeOutwardMaterialsLoading,
  );
  const finalizeOutwardProductsLoading = useSelector(
    (state: RootState) => state.newFloor.loading.finalizeOutwardProductsLoading,
  );

  const [searchParams, setSearchParams] = useSearchParams();
  // Tabs & Search State
  const [activeTab, setActiveTab] = useState<"batches" | "all_stock">(() => {
    const view = searchParams.get("view");
    if (view === "all_stock") return view;
    return "batches";
  });

  const updateParams = (tab: "batches" | "all_stock") => {
    const params = new URLSearchParams(searchParams);

    params.set("view", tab);
    params.delete("status");

    setSearchParams(params);
    setCurrentPage(1);
    setSearchTerm("");
    setDebouncedSearch("");
    setActiveTab(tab);
  };
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [openOutwardBatchesModal, setOpenOutwardBatchesModal] = useState<{
    id: string;
    type: "product" | "material";
  } | null>(null);
  // Filters State
  const [statusFilter, setStatusFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [page, setCurrentpage] = useState(1);
  const [selectedBatch, setSelectedBatch] = useState<IFloorRequest>();
  const [batchStatus, setBatchStatus] = useState<"fulfilled" | undefined>(
    undefined,
  );
  const [selectedReturnBatch, setSelectedReturnBatch] =
    useState<IFloorRequest | null>(null);
  const [selectedRequestId, setSelectedRequestid] = useState<
    | {
        requestNumber: string;
        id: string;
        type?: "product" | "material";
      }
    | undefined
  >();
  const [excelUploadBatchNo, setExcelUploadBatchNo] = useState<
    string | number | undefined
  >();
  const [batchToFinalize, setBatchToFinalize] = useState<IFloorRequest | null>(
    null,
  );
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Debounce search term to prevent excessive API hits
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchStock = useCallback(async () => {
    try {
      await dispatch(
        getFloorStock({
          params: {
            page,
            limit: 12,
            search: debouncedSearch,
          },
        }),
      ).unwrap();
    } catch (error) {
      console.error("Error fetching stock:", error);
    }
  }, [page, debouncedSearch, dispatch]);

  const fetchBatches = useCallback(async () => {
    try {
      await dispatch(
        getFloorBatches({
          params: {
            page,
            limit: 12,
            search: debouncedSearch,
            status: batchStatus,
          },
        }),
      ).unwrap();
    } catch (error) {
      console.error("Error fetching batches:", error);
    }
  }, [page, debouncedSearch, dispatch, batchStatus]);

  // Fetch Data from the API built earlier
  useEffect(() => {
    if (activeTab === "all_stock") {
      fetchStock();
    } else {
      fetchBatches();
    }
  }, [activeTab, fetchBatches, fetchStock]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setActiveDropdown(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function onViewDetails(item: IFloorRequest): void {
    setSelectedBatch(item);
  }

  function onFinalize(item: IFloorRequest): void {
    setBatchToFinalize(item);
  }

  async function handleConfirmFinalize(): Promise<void> {
    if (!batchToFinalize) return;
    try {
      if (batchToFinalize.type === "material") {
        await dispatch(
          finalizeOutwardMaterials({ requestId: batchToFinalize._id }),
        ).unwrap();
      } else if (batchToFinalize.type === "product") {
        await dispatch(
          finalizeOutwardProducts({ requestId: batchToFinalize._id }),
        ).unwrap();
      }
      await fetchBatches();
      setBatchToFinalize(null);
    } catch (error) {
      console.error("Error finalizing outward:", error);
    }
  }

  async function onViewOutwardBatches(item: IFloorRequest): Promise<void> {
    try {
      setOpenOutwardBatchesModal({ id: item._id, type: item.type });
    } catch (error) {
      console.error("Error viewing outward batches:", error);
    }
  }

  // Helper function to extract name from either productId or rawMaterialId
  const getItemName = (row: any) => {
    return row?.productId?.productName || row?.rawMaterialId?.name || null;
  };

  const handlShowFinalize = (item: IFloorRequest): boolean => {
    if (
      item.rawMaterialQuantity !== undefined &&
      item.producedQuanitity !== undefined
    ) {
      return item.rawMaterialQuantity <= item.producedQuanitity;
    }
    if (
      item.productQuantity !== undefined &&
      item.producedQuanitity !== undefined
    ) {
      return item.productQuantity <= item.producedQuanitity;
    }
    return false;
  };

  return (
    <div className="min-h-screen p-4 md:p-6 text-gray-800 mx-auto">
      {/* Navigation Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          <button
            onClick={() => updateParams("batches")}
            className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm transition-all ${
              activeTab === "batches"
                ? "border-indigo-600 text-indigo-600 font-semibold"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Batches
          </button>
          <button
            onClick={() => updateParams("all_stock")}
            className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm transition-all ${
              activeTab === "all_stock"
                ? "border-indigo-600 text-indigo-600 font-semibold"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            All Stock
          </button>
        </nav>
      </div>

      {/* Shared Search and Filters Bar */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6 border border-gray-100">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder={
                activeTab === "batches"
                  ? "Search by Batch No. or Username..."
                  : "Search across total stock..."
              }
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition"
            />
          </div>
          {/* {activeTab === "batches" && (
            <div
              onClick={() => {
                if (batchStatus) {
                  setBatchStatus(undefined);
                } else {
                  setBatchStatus("fulfilled");
                }
              }}
            >
              fullfiled
            </div>
          )} */}
          {activeTab === "batches" && (
            <div
              className="flex items-center gap-3 cursor-pointer select-none"
              onClick={() => {
                const newStatus =
                  batchStatus === "fulfilled" ? undefined : "fulfilled";

                setBatchStatus(newStatus);
              }}
            >
              <div
                className={`relative w-12 h-7 rounded-full transition-colors ${
                  batchStatus === "fulfilled" ? "bg-green-500" : "bg-slate-300"
                }`}
              >
                <div
                  className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${
                    batchStatus === "fulfilled"
                      ? "translate-x-5"
                      : "translate-x-0.5"
                  }`}
                />
              </div>

              <span className="font-medium text-slate-700">
                {batchStatus === "fulfilled" ? "Fulfilled" : "Pending"}
              </span>
            </div>
          )}
          {/* {activeTab === "batches" && (
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center justify-center gap-2 px-4 py-2 border rounded-lg text-sm font-medium transition ${
                showFilters
                  ? "bg-indigo-50 border-indigo-200 text-indigo-600"
                  : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
              }`}
            >
              <SlidersHorizontal className="h-4 w-4" />
              <span>Filters</span>
            </button>
          )} */}
        </div>

        {/* Expandable Filter Drawer Section */}
        {showFilters && activeTab === "batches" && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-100 transition-all">
            <div>
              <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full bg-gray-50 border border-gray-200 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="partially fulfilled">Partially Fulfilled</option>
                <option value="fulfilled">Fulfilled</option>
                <option value="rejected">Rejected</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Render Dynamic Content */}
      {activeTab === "batches" ? (
        loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <p className="text-gray-500 text-sm">
              No batch records match your criteria.
            </p>
          </div>
        ) : (
          <>
            {/* Desktop View (Hidden on Mobile) */}
            <div className="hidden md:block overflow-x-auto bg-white rounded-xl shadow-sm border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
                <thead className="bg-gray-50 text-gray-500 font-medium">
                  <tr>
                    <th className="px-6 py-3">Batch No</th>
                    <th className="px-6 py-3">Items</th>
                    <th className="px-6 py-3">Warehouse</th>
                    <th className="px-6 py-3">Requested By</th>
                    <th className="px-6 py-3">Status</th>
                    {/* <th className="px-6 py-3">Status</th> */}
                    <th className="px-6 py-3 relative">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {requests.map((item) => {
                    const itemName = getItemName(item);
                    let shouldFinalize = handlShowFinalize(item);
                    return (
                      <tr
                        onClick={() => {
                          if (batchStatus === undefined) onViewDetails(item);
                        }}
                        key={item._id}
                        className="hover:bg-gray-50 transition-colors cursor-pointer"
                      >
                        <td className="px-6 py-4 font-semibold text-gray-900">
                          #{item.batchNo}{" "}
                          {itemName ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800 border border-slate-200">
                              {itemName}
                            </span>
                          ) : null}
                        </td>
                        <td className="px-6 py-4 text-gray-600">
                          {item.items}
                        </td>
                        <td className="px-6 py-4 text-gray-600">
                          {item.warehouseDetails?.name || "N/A"}
                        </td>
                        <td className="px-6 py-4 text-gray-600">
                          {item.userDetails?.username || "System"}
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(item.status)}`}
                          >
                            {item.status}{" "}
                            {item.rawMaterialQuantity
                              ? `(${
                                  (item.rawMaterialQuantity ?? 0) -
                                  (item.producedQuanitity ?? 0)
                                })`
                              : ""}
                            {item.productQuantity
                              ? `(${
                                  (item.productQuantity ?? 0) -
                                  (item.producedQuanitity ?? 0)
                                })`
                              : ""}
                          </span>
                        </td>

                        <td className="px-6 py-4 text-right relative">
                          <ActionButtonMenu
                            itemId={item._id}
                            type={item.type}
                            activeDropdown={activeDropdown}
                            onViewOutwardBatches={() =>
                              onViewOutwardBatches(item)
                            }
                            onReplaceReturn={() => setSelectedReturnBatch(item)}
                            setActiveDropdown={setActiveDropdown}
                            onViewDetails={() => onViewDetails(item)}
                            onSendToStore={() => {
                              setSelectedRequestid({
                                id: item._id,
                                type: item.type,
                                requestNumber: (
                                  item.batchNo ?? "NA"
                                ).toString(),
                              });
                            }}
                            onUploadExcel={() => {
                              setExcelUploadBatchNo(item.batchNo ?? "NA");
                            }}
                            showActions={batchStatus === undefined}
                            shouldFinalize={shouldFinalize}
                            onFinalize={() => onFinalize(item)}
                            isFinalizing={
                              batchToFinalize?._id === item._id &&
                              (item.type === "material"
                                ? finalizeOutwardMaterialsLoading
                                : finalizeOutwardProductsLoading)
                            }
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile View (Cards Interface - Hidden on Desktop) */}
            <div className="grid grid-cols-1 gap-4 md:hidden">
              {requests.map((item) => {
                const itemName = getItemName(item);
                let shouldFinalize = handlShowFinalize(item);
                return (
                  <div
                    onClick={() => {
                      if (batchStatus === undefined) onViewDetails(item);
                    }}
                    key={item._id}
                    className="cursor-pointer bg-white p-4 rounded-xl border border-gray-200 shadow-sm relative"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <span className="text-xs text-gray-400 font-medium">
                          Batch
                        </span>
                        <h3 className="text-base font-bold text-gray-900">
                          #{item.batchNo}
                        </h3>
                      </div>
                      {itemName && (
                        <div>
                          <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
                            {itemName}
                          </span>
                        </div>
                      )}
                      <ActionButtonMenu
                        itemId={item._id}
                        type={item.type}
                        activeDropdown={activeDropdown}
                        setActiveDropdown={setActiveDropdown}
                        onReplaceReturn={() => setSelectedReturnBatch(item)}
                        onViewDetails={() => onViewDetails(item)}
                        showActions={batchStatus === undefined}
                        shouldFinalize={shouldFinalize}
                        onSendToStore={() => {
                          setSelectedRequestid({
                            id: item._id,
                            type: item.type,
                            requestNumber: (item.batchNo ?? "NA").toString(),
                          });
                        }}
                        onUploadExcel={() => {
                          setExcelUploadBatchNo(item.batchNo ?? "NA");
                        }}
                        onViewOutwardBatches={() => onViewOutwardBatches(item)}
                        onFinalize={() => onFinalize(item)}
                        isFinalizing={
                          batchToFinalize?._id === item._id &&
                          (item.type === "material"
                            ? finalizeOutwardMaterialsLoading
                            : finalizeOutwardProductsLoading)
                        }
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-xs mt-3 pt-3 border-t border-gray-50">
                      <div>
                        <p className="text-gray-400">Items</p>
                        <p className="font-medium text-gray-700">
                          {item.items}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-400">Requested By</p>
                        <p className="font-medium text-gray-700">
                          {item.userDetails?.username || "System"}
                        </p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-gray-400">Warehouse</p>
                        <p className="font-medium text-gray-700">
                          {item.warehouseDetails?.name || "N/A"}
                        </p>
                      </div>
                    </div>

                    <div className="mt-3 flex items-center justify-between">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(item.status)}`}
                      >
                        {item.status}{" "}
                        {item.rawMaterialQuantity
                          ? `(${
                              (item.rawMaterialQuantity ?? 0) -
                              (item.producedQuanitity ?? 0)
                            })`
                          : ""}
                        {item.productQuantity
                          ? `(${
                              (item.productQuantity ?? 0) -
                              (item.producedQuanitity ?? 0)
                            })`
                          : ""}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            {selectedBatch && (
              <FloorBatchEditDetailsModal
                onClose={async (success) => {
                  setSelectedBatch(undefined);

                  if (success) {
                    await fetchBatches();
                  }
                }}
                batch={selectedBatch}
              />
            )}
            {selectedRequestId && (
              <OutwardStarterPage
                requestId={selectedRequestId.id}
                type={selectedRequestId.type}
                isOpen={!!selectedRequestId}
                onClose={async (success) => {
                  setSelectedRequestid(undefined);
                  if (success) {
                    await fetchBatches();
                  }
                }}
                requestNumber={selectedRequestId.requestNumber}
              />
            )}
            {excelUploadBatchNo !== undefined && (
              <OutwardExcelUploadModal
                batchNo={excelUploadBatchNo}
                onClose={async (success) => {
                  setExcelUploadBatchNo(undefined);
                  if (success) {
                    await fetchBatches();
                  }
                }}
              />
            )}
            {openOutwardBatchesModal && (
              <BatchesViewModal
                isOpen={!!openOutwardBatchesModal}
                onClose={() => setOpenOutwardBatchesModal(null)}
                type={openOutwardBatchesModal?.type}
                requestId={openOutwardBatchesModal?.id}
              />
            )}
            {batchToFinalize && (
              <ConfirmationModal
                isOpen={!!batchToFinalize}
                title="Finalize Outward"
                subTitle={`Batch #${batchToFinalize.batchNo ?? "N/A"}`}
                description={`Are you sure you want to finalize outward for this ${
                  batchToFinalize.type === "material" ? "material" : "product"
                }? This action will complete the outward process.`}
                confirmText="Finalize"
                cancelText="Cancel"
                danger={false}
                loading={
                  batchToFinalize.type === "material"
                    ? finalizeOutwardMaterialsLoading
                    : finalizeOutwardProductsLoading
                }
                onConfirm={handleConfirmFinalize}
                onClose={() => setBatchToFinalize(null)}
              />
            )}
          </>
        )
      ) : (
        <FloorStockManager />
      )}
      {selectedReturnBatch && (
        <FloorMaterialReturnRequestModal
          batch={selectedReturnBatch}
          onClose={async (success) => {
            setSelectedReturnBatch(null);
            if (success) await fetchBatches();
          }}
        />
      )}
      <PaginationControls
        currentPage={page}
        onPageChange={setCurrentpage}
        totalPages={pagination.getFloorBatchesPagination?.totalPages || 0}
        isLoading={loading}
        totalItems={pagination.getFloorBatchesPagination?.totalCount}
      />
    </div>
  );
};

// --- Dropdown Menu Helper Component ---
interface ActionMenuProps {
  itemId: string;
  type?: "product" | "material";
  showActions?: boolean;
  activeDropdown: string | null;
  setActiveDropdown: (id: string | null) => void;
  // 1. Add optional callback props for each action
  onViewDetails: () => void;
  onSendToStore: () => void;
  onUploadExcel: () => void;
  onFinalize?: () => void;
  onReplaceReturn: () => void;
  onViewOutwardBatches: () => void;
  isFinalizing?: boolean;
  shouldFinalize: boolean;
  // onEdit?: (itemId: string) => void;
  // onAddMaterials?: (itemId: string) => void;
}

function ActionButtonMenu({
  itemId,
  type,
  showActions = true,
  activeDropdown,
  setActiveDropdown,
  onViewDetails,
  onSendToStore,
  onUploadExcel,
  onFinalize,
  onViewOutwardBatches,
  isFinalizing,
  shouldFinalize,
  onReplaceReturn,
}: ActionMenuProps) {
  const { role } = useSelector((state: RootState) => state?.auth);
  const isOpen = activeDropdown === itemId;

  const close = () => setActiveDropdown(null);

  const actions = [
    {
      label: "View Details",
      description: "See full batch information",
      icon: Eye,
      onClick: onViewDetails,
      classes: "bg-indigo-50 text-indigo-600",
      disabled: false,
    },
    {
      label: "Replace/Return material",
      description: "Replace or return assigned material in this batch",
      icon: RefreshCcw,
      onClick: onReplaceReturn,
      classes: "bg-red-50 text-red-600",
      disabled: false,
    },
    ...(!shouldFinalize
      ? [
          {
            label: "Send to store",
            description: "Move this batch to a warehouse",
            icon: Box,
            onClick: onSendToStore,
            classes: "bg-emerald-50 text-emerald-600",
            disabled: false,
          },
        ]
      : []),
    ...(role === "admin" || role === "store" || role === "factory"
      ? [
          {
            label: "Upload Excel",
            description: "Attach an outward Excel sheet",
            icon: FileUp,
            onClick: onUploadExcel,
            classes: "bg-amber-50 text-amber-600",
            disabled: false,
          },
        ]
      : []),
  ];

  return (
    <>
      <div className="flex items-center justify-end gap-2">
        {type && (
          <>
            <button
              type="button"
              title="View Batches"
              aria-label="View Batches"
              onClick={(e) => {
                e.stopPropagation();
                onViewOutwardBatches();
              }}
              className="group relative flex items-center gap-1.5 px-2 min-[875px]:px-3 py-1.5 rounded-lg border border-purple-200 text-purple-600 text-sm font-medium hover:bg-purple-50 transition focus:outline-none"
            >
              <List className="h-4 w-4" />
              <span className="hidden min-[875px]:inline">View Batches</span>
              <span className="pointer-events-none absolute right-0 top-full z-50 mt-2 hidden w-max rounded bg-gray-900 px-2 py-1 text-xs font-normal text-white shadow-lg group-hover:block group-focus-within:block min-[875px]:hidden">
                View Batches
              </span>
            </button>
            {showActions && shouldFinalize && (
              <button
                type="button"
                title={isFinalizing ? "Finalizing" : "Finalize"}
                aria-label={isFinalizing ? "Finalizing" : "Finalize"}
                onClick={(e) => {
                  e.stopPropagation();
                  onFinalize?.();
                }}
                disabled={isFinalizing}
                className="group relative flex items-center gap-1.5 px-2 min-[875px]:px-3 py-1.5 rounded-lg border border-blue-200 text-blue-600 text-sm font-medium hover:bg-blue-50 transition disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none"
              >
                <CheckCircle2 className="h-4 w-4" />
                <span className="hidden min-[875px]:inline">
                  {isFinalizing ? "Finalizing..." : "Finalize"}
                </span>
                <span className="pointer-events-none absolute right-0 top-full z-50 mt-2 hidden w-max rounded bg-gray-900 px-2 py-1 text-xs font-normal text-white shadow-lg group-hover:block group-focus-within:block min-[875px]:hidden">
                  {isFinalizing ? "Finalizing" : "Finalize"}
                </span>
              </button>
            )}
          </>
        )}
        {showActions && (
          <button
            type="button"
            title="Actions"
            aria-label="Actions"
            onClick={(e) => {
              e.stopPropagation();
              setActiveDropdown(itemId);
            }}
            className="group relative flex items-center gap-1.5 px-2 min-[875px]:px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 text-sm font-medium hover:text-indigo-600 hover:border-indigo-200 hover:bg-indigo-50 transition focus:outline-none"
          >
            <MoreVertical className="h-4 w-4" />
            <span className="hidden min-[875px]:inline">Actions</span>
            <span className="pointer-events-none absolute right-0 top-full z-50 mt-2 hidden w-max rounded bg-gray-900 px-2 py-1 text-xs font-normal text-white shadow-lg group-hover:block group-focus-within:block min-[875px]:hidden">
              Actions
            </span>
          </button>
        )}
      </div>

      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-[2px]"
          onClick={(e) => {
            e.stopPropagation();
            close();
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:w-80 bg-white rounded-t-2xl sm:rounded-2xl shadow-xl overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-200"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900">Actions</h3>
              <button
                onClick={close}
                className="p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="p-2">
              {actions.map(
                ({
                  label,
                  description,
                  icon: Icon,
                  onClick,
                  classes,
                  disabled,
                }) => (
                  <button
                    key={label}
                    disabled={disabled}
                    onClick={() => {
                      if (disabled) return;
                      onClick();
                      close();
                    }}
                    className="flex items-center w-full gap-3 px-3 py-3 rounded-xl text-left hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                  >
                    <span
                      className={`flex items-center justify-center h-10 w-10 rounded-full shrink-0 ${classes}`}
                    >
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-gray-900">
                        {label}
                      </span>
                      <span className="block text-xs text-gray-400 truncate">
                        {description}
                      </span>
                    </span>
                  </button>
                ),
              )}
            </div>

            <div className="p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:hidden">
              <button
                onClick={close}
                className="w-full py-2.5 rounded-xl bg-gray-100 text-sm font-medium text-gray-700 hover:bg-gray-200 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
export default NewFloorStock;
